import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { SignedIn, SignedOut, RedirectToSignIn, useAuth } from '@clerk/clerk-react'
import { Layout } from '@/components/Layout'
import { ChatPage } from '@/pages/ChatPage'
import { UserProfilePage } from '@/pages/UserProfilePage'
import { SessionProvider } from '@/providers/SessionProvider'
import { ErrorProvider } from '@/providers/ErrorProvider'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Toaster } from '@/components/ui/toaster'
import { NotFoundDisplay } from '@/components/ErrorDisplay'
import { GlobalErrorHandler } from '@/components/GlobalErrorHandler'
import { PWAInstallPrompt } from '@/components/PWAInstallPrompt'
import { PWAUpdatePrompt } from '@/components/PWAUpdatePrompt'

import config from '@/config'
import './App.css'

function ChatPageWithKey() {
  const { sessionId } = useParams();
  return <ChatPage key={sessionId || 'no-session'} />;
}

function ChatPageWrapper() {
  return (
    <SessionProvider>
      <ChatPage />
    </SessionProvider>
  );
}

function ChatPageWithSessionWrapper() {
  return (
    <SessionProvider>
      <ChatPageWithKey />
    </SessionProvider>
  );
}

function ProtectedRoutes() {
  return (
    <>
      <GlobalErrorHandler />
      <div className="h-screen bg-background text-foreground overflow-hidden">
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<Navigate to="/chat" replace />} />
            <Route path="chat" element={<ChatPageWrapper />} />
            <Route path="chat/:sessionId" element={<ChatPageWithSessionWrapper />} />
          </Route>
          <Route path="/user-profile" element={<UserProfilePage />} />
          <Route path="*" element={<NotFoundDisplay />} />
        </Routes>
      </div>
      <Toaster />
      <PWAInstallPrompt />
      <PWAUpdatePrompt />
    </>
  )
}

function App() {
  // Require Clerk to be configured for authentication
  if (!config.CLERK_PUBLISHABLE_KEY) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <h2 className="text-2xl font-bold mb-4 text-destructive">
            Authentication Required
          </h2>
          <p className="text-muted-foreground mb-4">
            This application requires authentication to be configured. Please set up Clerk authentication.
          </p>
          <div className="text-left bg-muted p-4 rounded-lg">
            <p className="text-sm font-medium mb-2">Required environment variable:</p>
            <code className="text-xs bg-background px-2 py-1 rounded">
              VITE_CLERK_PUBLISHABLE_KEY
            </code>
          </div>
        </div>
      </div>
    )
  }

  return <ClerkApp />
}

function ClerkApp() {
  // Only call useAuth when we know Clerk is configured
  const { isLoaded } = useAuth()

  // Show loading while Clerk is initializing
  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <ErrorProvider>
        <Router>
          <SignedIn>
            <ProtectedRoutes />
          </SignedIn>
          <SignedOut>
            <RedirectToSignIn />
          </SignedOut>
        </Router>
      </ErrorProvider>
    </ErrorBoundary>
  )
}

export default App